import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { useConfirm } from '../contexts/ConfirmContext';
import { playSound, SFX_KEYS } from '../game/systems/SoundManager';

// ============ TYPES ============

interface CrewTrait {
  id: string;
  name: string;
  description: string;
  effectPerLevel: number;
  statAffected: string;
}

interface CrewFlaw {
  id: string;
  name: string;
  description: string;
  penalty: number;
  condition?: string;
}

interface TavernRecruit {
  id: string;
  name: string;
  portrait: string;
  trait: CrewTrait;
  flaw: CrewFlaw;
  synergyTag?: string;
  recruitCost: number;
  expiresAt: number;
}

interface CrewMember {
  id: string;
  name: string;
  portrait: string;
  level: number;
  xp: number;
  xpToNextLevel: number;
  trait: CrewTrait;
  flaw: CrewFlaw;
  synergyTag?: string;
  recruitedAt: number;
  combatParticipation: number;
  contractsCompleted: number;
}

interface TavernPanelProps {
  socket: Socket | null;
  isOpen: boolean;
  onClose: () => void;
  pieces: number;
  tavernId: string;
  tavernName: string;
}

// ============ SYNERGY TAG COLORS ============

const SYNERGY_COLORS: Record<string, string> = {
  veteran: '#b8860b',
  rookie: '#90ee90',
  pirate: '#dc143c',
  navy: '#4169e1',
  merchant: '#ffd700',
  scholar: '#9370db',
  brawler: '#ff6347',
  mystic: '#00ced1',
};

// ============ COMPONENT ============

export const TavernPanel: React.FC<TavernPanelProps> = ({
  socket,
  isOpen,
  onClose,
  pieces,
  tavernId,
  tavernName,
}) => {
  const { confirm } = useConfirm();
  const [recruits, setRecruits] = useState<TavernRecruit[]>([]);
  const [playerCrew, setPlayerCrew] = useState<CrewMember[]>([]);
  const [maxSlots, setMaxSlots] = useState<number>(3);
  const [selectedRecruit, setSelectedRecruit] = useState<TavernRecruit | null>(null);
  const [selectedCrew, setSelectedCrew] = useState<CrewMember | null>(null);
  const [message, setMessage] = useState<string>('');
  const [nextRefreshAt, setNextRefreshAt] = useState<number>(0);
  const [refreshCountdown, setRefreshCountdown] = useState<string>('');
  const [isHelpOpen, setIsHelpOpen] = useState<boolean>(false);

  const formatStatLabel = (statKey: string): string => {
    const withSpaces = statKey.replace(/([a-z])([A-Z])/g, '$1 $2');
    return withSpaces
      .split(' ')
      .filter((p) => p.length > 0)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  // Request tavern data on open
  useEffect(() => {
    if (!socket || !isOpen) return;

    socket.emit('tavern:request', { tavernId, locationName: tavernName });
    socket.emit('shipcrew:request');

    const handleTavernData = (data: {
      tavernId: string;
      locationName: string;
      recruits: TavernRecruit[];
      nextRefreshAt: number;
    }) => {
      console.log(`🍺 TavernPanel received tavern data: ${data.recruits.length} recruits`);
      setRecruits(data.recruits);
      setNextRefreshAt(data.nextRefreshAt);
    };

    const handleCrewState = (data: {
      crew: CrewMember[];
      maxSlots: number;
      bonuses: Record<string, number>;
    }) => {
      setPlayerCrew(data.crew);
      setMaxSlots(data.maxSlots);
    };

    const handleRecruitResult = (result: {
      success: boolean;
      message: string;
      crew?: CrewMember;
    }) => {
      setMessage(result.message);
      if (result.success) {
        playSound(SFX_KEYS.CREW_RECRUIT);
        setSelectedRecruit(null);
      }
      setTimeout(() => setMessage(''), 3000);
    };

    const handleDismissResult = (result: { success: boolean; message: string }) => {
      setMessage(result.message);
      if (result.success) {
        setSelectedCrew(null);
      }
      setTimeout(() => setMessage(''), 3000);
    };

    socket.on('tavern:data', handleTavernData);
    socket.on('crew:state', handleCrewState);
    socket.on('tavern:recruitResult', handleRecruitResult);
    socket.on('shipcrew:dismissResult', handleDismissResult);

    return () => {
      socket.off('tavern:data', handleTavernData);
      socket.off('crew:state', handleCrewState);
      socket.off('tavern:recruitResult', handleRecruitResult);
      socket.off('shipcrew:dismissResult', handleDismissResult);
    };
  }, [socket, isOpen, tavernId, tavernName]);

  // Refresh countdown timer
  useEffect(() => {
    if (!nextRefreshAt) return;

    const updateCountdown = (): void => {
      const remaining = Math.max(0, nextRefreshAt - Date.now());
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      setRefreshCountdown(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [nextRefreshAt]);

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      if (isHelpOpen) {
        setIsHelpOpen(false);
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isHelpOpen, onClose]);

  const handleRecruit = useCallback((recruit: TavernRecruit) => {
    if (!socket) return;
    if (playerCrew.length >= maxSlots) {
      setMessage('Crew is full! Dismiss a member first.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    if (pieces < recruit.recruitCost) {
      setMessage(`Not enough pieces! Need ${recruit.recruitCost.toLocaleString()}`);
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    socket.emit('tavern:recruit', { tavernId, recruitId: recruit.id });
  }, [socket, tavernId, playerCrew.length, maxSlots, pieces]);

  const handleDismiss = useCallback((crew: CrewMember) => {
    if (!socket) return;
    void (async () => {
      const ok: boolean = await confirm({
        title: 'Dismiss Crew',
        message: `Dismiss ${crew.name}? This cannot be undone.`,
        confirmText: 'Dismiss',
        cancelText: 'Cancel',
        tone: 'danger',
      });
      if (!ok) return;
      socket.emit('shipcrew:dismiss', { crewId: crew.id });
    })();
  }, [socket, confirm]);

  const renderPortrait = (portrait: string, name: string): JSX.Element => {
    const publicUrl: string = process.env.PUBLIC_URL || '';
    return (
      <img
        src={`${publicUrl}${portrait}`}
        alt={name}
        style={{
          width: 64,
          height: 64,
          objectFit: 'cover',
          border: '2px solid #444',
        }}
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.onerror = null;
          target.src = `${publicUrl}/assets/portraits/pirates/pirate_1.png`;
        }}
      />
    );
  };

  const renderSynergyTag = (tag: string): JSX.Element => {
    const color: string = SYNERGY_COLORS[tag] || '#8b949e';
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '2px 6px',
          border: `1px solid ${color}`,
          color,
          background: '#0d1117',
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {tag}
      </div>
    );
  };

  const renderHelpModal = (): JSX.Element | null => {
    if (!isHelpOpen) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1100,
        }}
        onClick={() => setIsHelpOpen(false)}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 720,
            maxWidth: '95vw',
            maxHeight: '85vh',
            overflow: 'auto',
            background: '#0d1117',
            border: '2px solid #30363d',
            fontFamily: 'var(--font-mono)',
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              background: '#161b22',
              borderBottom: '1px solid #30363d',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 'bold', color: '#fff' }}>Crew Traits & Flaws</div>
            <button
              onClick={() => setIsHelpOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#8b949e',
                cursor: 'pointer',
                fontSize: 18,
                padding: 0,
                fontFamily: 'inherit',
              }}
            >
              X
            </button>
          </div>

          <div style={{ padding: 16, color: '#c9d1d9', fontSize: 12, lineHeight: 1.5 }}>
            <div style={{ marginBottom: 12, color: '#8b949e' }}>
              Traits (perks) and flaws scale your ship’s performance while the crew member is onboard.
              Synergy tags are quick labels that help you build themed crews.
            </div>

            <div style={{ fontSize: 12, fontWeight: 'bold', color: '#fff', marginBottom: 6 }}>Synergy Tags</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {Object.keys(SYNERGY_COLORS).map((tag) => (
                <div key={tag} style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 120 }}>
                  {renderSynergyTag(tag)}
                  {/* <div style={{ fontSize: 10, color: '#8b949e' }}>Build bonuses via crew composition.</div> */}
                </div>
              ))}
            </div>

            <div style={{ fontSize: 12, fontWeight: 'bold', color: '#fff', marginBottom: 6 }}>Traits (Perks)</div>
            <div style={{ marginBottom: 12, color: '#8b949e' }}>
              Each crew member has a trait that grants a bonus per level.
              Example: <span style={{ color: '#4caf50' }}>Gunner: +8% Cannon Damage/lvl</span>
            </div>

            <div style={{ fontSize: 12, fontWeight: 'bold', color: '#fff', marginBottom: 6 }}>Flaws</div>
            <div style={{ marginBottom: 12, color: '#8b949e' }}>
              Each crew member has a flaw that applies a penalty (some may have special conditions).
              Example: <span style={{ color: '#ff6b6b' }}>Superstitious: -5%</span>
            </div>

            <div style={{ fontSize: 12, fontWeight: 'bold', color: '#fff', marginBottom: 6 }}>Costs</div>
            <div style={{ color: '#8b949e' }}>
              Recruiting costs <span style={{ color: '#ffd700' }}>₱</span>. If you can't afford a recruit, the card will show the cost in red.
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRecruitCard = (recruit: TavernRecruit): JSX.Element => {
    const canAfford = pieces >= recruit.recruitCost;
    const crewFull = playerCrew.length >= maxSlots;
    const isSelected = selectedRecruit?.id === recruit.id;

    return (
      <div
        key={recruit.id}
        onClick={() => setSelectedRecruit(isSelected ? null : recruit)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: 8,
          background: isSelected ? '#1a3a5c' : '#1a1a2e',
          border: `2px solid ${isSelected ? '#4a9eff' : '#333'}`,
          cursor: 'pointer',
          opacity: crewFull ? 0.5 : 1,
        }}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          {renderPortrait(recruit.portrait, recruit.name)}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold', fontSize: 14, color: '#fff' }}>
              {recruit.name}
            </div>
            {recruit.synergyTag && (
              <div style={{ marginTop: 4 }}>{renderSynergyTag(recruit.synergyTag)}</div>
            )}
            <div style={{ fontSize: 11, color: '#4caf50', marginTop: 4 }}>
              {recruit.trait.name}
            </div>
            <div style={{ fontSize: 10, color: '#888' }}>
              +{recruit.trait.effectPerLevel}% {formatStatLabel(recruit.trait.statAffected)}/lvl
            </div>
          </div>
        </div>
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div
            style={{
              fontSize: 10,
              color: '#ffb4b4',
              background: '#3a1a1a',
              border: '1px solid #7a2a2a',
              padding: '2px 6px',
              display: 'inline-block',
            }}
          >
            Flaw: {recruit.flaw.name}
          </div>
          <div style={{ fontSize: 12, color: canAfford ? '#ffd700' : '#ff4444' }}>
            {recruit.recruitCost.toLocaleString()} ₱
          </div>
        </div>
        {isSelected && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRecruit(recruit);
            }}
            disabled={!canAfford || crewFull}
            style={{
              marginTop: 8,
              padding: '6px 12px',
              background: canAfford && !crewFull ? '#4caf50' : '#555',
              color: '#fff',
              border: 'none',
              cursor: canAfford && !crewFull ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
            }}
          >
            {crewFull ? 'CREW FULL' : canAfford ? 'RECRUIT' : 'CANNOT AFFORD'}
          </button>
        )}
      </div>
    );
  };

  const renderCrewCard = (crew: CrewMember): JSX.Element => {
    const isSelected = selectedCrew?.id === crew.id;
    const xpProgress = crew.xpToNextLevel > 0 
      ? ((crew.xp - (crew.level > 1 ? getXpForLevel(crew.level) : 0)) / 
         (crew.xpToNextLevel - (crew.level > 1 ? getXpForLevel(crew.level) : 0))) * 100
      : 100;

    return (
      <div
        key={crew.id}
        onClick={() => setSelectedCrew(isSelected ? null : crew)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: 8,
          background: isSelected ? '#3a1a1a' : '#1a1a2e',
          border: `2px solid ${isSelected ? '#ff6b6b' : '#333'}`,
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          {renderPortrait(crew.portrait, crew.name)}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 'bold', fontSize: 14, color: '#fff' }}>
                {crew.name}
              </div>
              <div style={{ fontSize: 12, color: '#ffd700' }}>
                Lv.{crew.level}
              </div>
            </div>
            {crew.synergyTag && (
              <div style={{ marginTop: 4 }}>{renderSynergyTag(crew.synergyTag)}</div>
            )}
            <div style={{ fontSize: 11, color: '#4caf50', marginTop: 4 }}>
              {crew.trait.name}: +{crew.trait.effectPerLevel * crew.level}%
            </div>
            <div style={{ fontSize: 9, color: '#ff6b6b' }}>
              {crew.flaw.name}: -{crew.flaw.penalty}%
            </div>
          </div>
        </div>
        {/* XP Bar */}
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 10, color: '#888', marginBottom: 2 }}>
            XP: {crew.xp} / {crew.xpToNextLevel || 'MAX'}
          </div>
          <div style={{ height: 4, background: '#333', width: '100%' }}>
            <div
              style={{
                height: '100%',
                width: `${Math.min(100, xpProgress)}%`,
                background: crew.level >= 5 ? '#ffd700' : '#4caf50',
              }}
            />
          </div>
        </div>
        {isSelected && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss(crew);
            }}
            style={{
              marginTop: 8,
              padding: '6px 12px',
              background: '#dc3545',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            DISMISS
          </button>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      {renderHelpModal()}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#0d1117',
          border: '2px solid #30363d',
          width: 800,
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'var(--font-mono)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '12px 16px',
            background: '#161b22',
            borderBottom: '1px solid #30363d',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 'bold', color: '#fff' }}>
              {tavernName}
            </div>
            <div style={{ fontSize: 12, color: '#888' }}>
              Recruit crew members for your ship
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ fontSize: 14, color: '#ffd700' }}>
              {pieces.toLocaleString()}₱
            </div>
            <button
              onClick={() => setIsHelpOpen(true)}
              style={{
                background: '#0d1117',
                border: '1px solid #30363d',
                color: '#c9d1d9',
                cursor: 'pointer',
                padding: '4px 8px',
                fontFamily: 'inherit',
                fontSize: 12,
              }}
            >
              INFO
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#888',
                fontSize: 24,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              X
            </button>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div
            style={{
              padding: '8px 16px',
              background: message.includes('!') ? '#3a1a1a' : '#1a3a1a',
              color: message.includes('!') ? '#ff6b6b' : '#4caf50',
              fontSize: 12,
            }}
          >
            {message}
          </div>
        )}

        {/* Content */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Available Recruits */}
          <div style={{ flex: 1, borderRight: '1px solid #30363d', display: 'flex', flexDirection: 'column' }}>
            <div
              style={{
                padding: '8px 12px',
                background: '#161b22',
                borderBottom: '1px solid #30363d',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 'bold', color: '#fff' }}>
                Available Recruits
              </div>
              <div style={{ fontSize: 11, color: '#888' }}>
                Refresh in {refreshCountdown}
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recruits.length === 0 ? (
                <div style={{ color: '#888', textAlign: 'center', padding: 20 }}>
                  No recruits available
                </div>
              ) : (
                recruits.map(renderRecruitCard)
              )}
            </div>
          </div>

          {/* Your Crew */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div
              style={{
                padding: '8px 12px',
                background: '#161b22',
                borderBottom: '1px solid #30363d',
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 'bold', color: '#fff' }}>
                Your Crew ({playerCrew.length}/{maxSlots})
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {playerCrew.length === 0 ? (
                <div style={{ color: '#888', textAlign: 'center', padding: 20 }}>
                  No crew members yet
                </div>
              ) : (
                playerCrew.map(renderCrewCard)
              )}
              {/* Empty slots */}
              {Array.from({ length: maxSlots - playerCrew.length }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  style={{
                    padding: 16,
                    background: '#0a0a14',
                    border: '2px dashed #333',
                    textAlign: 'center',
                    color: '#555',
                    fontSize: 12,
                  }}
                >
                  Empty Slot
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer with info */}
        <div
          style={{
            padding: '8px 16px',
            background: '#161b22',
            borderTop: '1px solid #30363d',
            fontSize: 11,
            color: '#666',
          }}
        >
          Crew members gain XP from combat and contracts. Max level: 5. Crew are lost if your ship sinks without insurance.
        </div>
      </div>
    </div>
  );
};

// Helper to get XP threshold for a level
const getXpForLevel = (level: number): number => {
  const table: Record<number, number> = { 1: 0, 2: 100, 3: 300, 4: 600, 5: 1000 };
  return table[level] || 0;
};

export default TavernPanel;
